import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import SearchableSelect from '../common/SearchableSelect';
import {
  buildMilestoneEmailSelectOptions,
  buildMilestoneNotificationSelectOptions,
  getMilestoneDateInputType,
  milestoneInputToIso,
  normalizeMilestoneForEdit,
  toDateInputValue,
  toDatetimeLocalValue,
  validateAndCleanupMilestone
} from './milestoneDateHelpers';
import './MilestoneDateModal.scss';

const toEditorDateValue = (iso, inputType) =>
  inputType === 'date' ? toDateInputValue(iso) : toDatetimeLocalValue(iso);

/**
 * Edit a contract milestone group (start / end / notify / options) with form-parity validations.
 */
const MilestoneDateModal = ({
  show,
  config,
  milestone,
  buyerEmail,
  appChoices,
  onHide,
  onSave
}) => {
  const inputType = getMilestoneDateInputType(config);
  const emailChoices = useMemo(() => buildMilestoneEmailSelectOptions(appChoices), [appChoices]);
  const optionChoices = useMemo(
    () => buildMilestoneNotificationSelectOptions(appChoices, config),
    [appChoices, config]
  );

  const [draft, setDraft] = useState(() =>
    normalizeMilestoneForEdit(milestone, buyerEmail)
  );

  useEffect(() => {
    if (!show) return;
    const normalized = normalizeMilestoneForEdit(milestone, buyerEmail);
    setDraft({
      ...normalized,
      start: toEditorDateValue(normalized.start, inputType),
      end: toEditorDateValue(normalized.end, inputType)
    });
  }, [show, milestone, buyerEmail, inputType]);

  if (!config) return null;

  const updateField = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    const prepared = {
      start: milestoneInputToIso(draft.start, config),
      end: config.end ? milestoneInputToIso(draft.end, config) : '',
      notificationEmails: draft.notificationEmails || [],
      options: draft.options || []
    };

    const result = validateAndCleanupMilestone(prepared, config, buyerEmail);
    if (!result.ok) {
      toast.error(result.message);
      return;
    }

    if (onSave) onSave(result.value);
    if (onHide) onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" className="milestone-date-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <h6 className="mb-0">{config.name} date</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>{config.name} start</Form.Label>
          <Form.Control
            type={inputType}
            value={draft.start || ''}
            onChange={e => updateField('start', e.target.value)}
          />
        </Form.Group>

        {config.end && (
          <Form.Group className="mb-3">
            <Form.Label>{config.name} end</Form.Label>
            <Form.Control
              type={inputType}
              value={draft.end || ''}
              onChange={e => updateField('end', e.target.value)}
            />
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Notify {config.name.toLowerCase()} to</Form.Label>
          <SearchableSelect
            options={emailChoices}
            value={draft.notificationEmails || []}
            onChange={next => updateField('notificationEmails', next)}
            placeholder="Select who to notify..."
            isMulti
            isClearable
            size="sm"
          />
        </Form.Group>

        {config.options && (
          <Form.Group className="mb-0">
            <Form.Label>{config.name} notification options</Form.Label>
            <SearchableSelect
              options={optionChoices}
              value={draft.options || []}
              onChange={next => updateField('options', next)}
              placeholder="Select options..."
              isMulti
              isClearable
              size="sm"
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" onClick={handleApply}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MilestoneDateModal;
